import { isCartEntryEqual } from './cart-entry';
import { ICart, ILegacyCartEntry } from './types';
import { removeElementAtIndexFromList, replaceElementAtIndex } from './utils';

type ICartEntryUpdates = Omit<ILegacyCartEntry, 'referenceId' | 'details' | 'type'>;

/**
 * Helper function to get the default predicate.
 */
const parsePredicateArg = (arg: ILegacyCartEntry | ((ce: ILegacyCartEntry) => boolean)) =>
  typeof arg === 'function' ? arg : (ce: ILegacyCartEntry) => isCartEntryEqual(ce, arg);

/**
 * Finds the cart entry.
 */
export function findCartEntry(
  cart: ICart,
  cartEntry: ILegacyCartEntry
): ILegacyCartEntry | undefined;
export function findCartEntry(
  cart: ICart,
  predicate: (ce: ILegacyCartEntry) => boolean
): ILegacyCartEntry | undefined;
export function findCartEntry(
  cart: ICart,
  arg: ILegacyCartEntry | ((ce: ILegacyCartEntry) => boolean)
): ILegacyCartEntry | undefined {
  return cart.find(parsePredicateArg(arg));
}

export const existCartEntry = (
  cart: ICart,
  predicate: (ce: ILegacyCartEntry, idx: number, cart: ICart) => boolean
) => !!cart.find(predicate);

/**
 * Adds cart entries to the cart.
 */
export const addToCart = (cart: ICart, ...cartEntry: ILegacyCartEntry[]): ICart =>
  cart.concat(cartEntry);

/**
 * Filter the cart entries if satisfy the predicate.
 */
export const filterCartEntries = (
  cart: ICart,
  predicate: (ce: ILegacyCartEntry) => boolean
): ICart => cart.filter(predicate);

/**
 * Removes the given cart entry from the cart.
 * If no cart entry if found returns the same list.
 */
export function removeCartEntry(cart: ICart, cartEntry: ILegacyCartEntry): ICart;
export function removeCartEntry(cart: ICart, predicate: (ce: ILegacyCartEntry) => boolean): ICart;
export function removeCartEntry(
  cart: ICart,
  arg: ILegacyCartEntry | ((ce: ILegacyCartEntry) => boolean)
): ICart {
  const entryIndex = cart.findIndex(parsePredicateArg(arg));

  return removeElementAtIndexFromList(cart, entryIndex);
}

/**
 * Update a cart entry based on a given cart entry to replace or a predicate function.
 */
export function updateCartEntry(
  cart: ICart,
  cartEntry: ILegacyCartEntry,
  cartEntryUpdates: ICartEntryUpdates
): ICart;
export function updateCartEntry(
  cart: ICart,
  predicate: (ce: ILegacyCartEntry) => boolean,
  cartEntryUpdates: ICartEntryUpdates
): ICart;
export function updateCartEntry(
  cart: ICart,
  arg: ILegacyCartEntry | ((ce: ILegacyCartEntry) => boolean),
  cartEntryUpdates: ICartEntryUpdates
) {
  const index = cart.findIndex(parsePredicateArg(arg));
  const foundedCartEntry = cart[index];

  // invalid index
  if (!foundedCartEntry) {
    return cart;
  }

  const updatedCartEntry = {
    ...foundedCartEntry,
    ...cartEntryUpdates,
  };

  return replaceElementAtIndex(cart, updatedCartEntry, index);
}
